import {
    createStyles,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    SwipeableDrawer,
    WithStyles,
    withStyles,
} from "@material-ui/core";
import {
    Divider,
    EllipsisVerticalIcon,
    NewMessageIcon,
    PageActionButton,
    RelodeTheme,
    ShareAltIcon,
    useDeviceSize,
} from "@relode/core";
import * as React from "react";

const styles = (theme: RelodeTheme) =>
    createStyles({
        mobileOptions: {
            position: "absolute",
            right: 0,
            top: 0,
            marginRight: theme.spacing(2),
        },
        pageActionButtonsContainer: {
            display: "flex",
        },
    });

interface Props extends WithStyles<typeof styles> {
    shareAction?: (boolean) => void;
    messageAction?: (boolean) => void;
}

export const CandidateActionOptions = withStyles(styles, {
    name: "CandidateActionOptions",
})((props: Props) => {
    const device = useDeviceSize();
    const { classes, shareAction, messageAction } = props;
    const [drawerIsOpen, openDrawer] = React.useState(() => false);

    //Material's recommends checking for iOS users when using their SwipeableDrawer
    //The following properties are used in this documentation website for optimal usability of the component: https://material-ui.com/components/drawers/
    //iOS is hosted on high-end devices. The backdrop transition can be enabled without dropping frames. The performance will be good enough.
    //iOS has a "swipe to go back" feature that interferes with the discovery feature, so discovery has to be disabled.

    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    const renderMobile = () => (
        <div className={classes.mobileOptions}>
            <>
                <IconButton
                    onClick={() => openDrawer(true)}
                    edge="end"
                    id="mobileOptions"
                    color="primary"
                    title="open more action options"
                >
                    <EllipsisVerticalIcon />
                </IconButton>
                <SwipeableDrawer
                    anchor="bottom"
                    open={drawerIsOpen}
                    onOpen={() => openDrawer(true)}
                    onClose={() => openDrawer(false)}
                    disableBackdropTransition={!iOS}
                    disableDiscovery={true}
                >
                    <div
                        role="presentation"
                        onClick={() => openDrawer(false)}
                        onKeyDown={() => openDrawer(false)}
                    >
                        <List>
                            {!!messageAction ? (
                                <ListItem
                                    button
                                    onClick={() => messageAction(true)}
                                >
                                    <ListItemIcon>
                                        <NewMessageIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="message" />
                                </ListItem>
                            ) : null}
                            {!!shareAction ? (
                                <ListItem
                                    button
                                    onClick={() => shareAction(true)}
                                >
                                    <ListItemIcon>
                                        <ShareAltIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="share" />
                                </ListItem>
                            ) : null}
                        </List>
                        <Divider />
                    </div>
                </SwipeableDrawer>
            </>
        </div>
    );

    const renderDesktop = () => (
        <div className={classes.pageActionButtonsContainer}>
            {!!messageAction ? (
                <PageActionButton
                    onClick={() => messageAction(true)}
                    id="sendMessage"
                    color="primary"
                    title="send a message"
                >
                    <NewMessageIcon />
                </PageActionButton>
            ) : null}
            {!!shareAction ? (
                <PageActionButton
                    onClick={() => shareAction(true)}
                    id="share"
                    color="primary"
                    title="share candidate with colleagues"
                >
                    <ShareAltIcon />
                </PageActionButton>
            ) : null}
        </div>
    );

    const hasActions = !!messageAction || !!shareAction;

    return hasActions
        ? device === "mobile"
            ? renderMobile()
            : renderDesktop()
        : null;
});
